import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';

const useStyles = makeStyles(() => ({
  root: {
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
    flexShrink: 0,
  },
  label: {
    fontSize: 10,
    fontWeight: 600,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    padding: '8px 14px 4px',
  },
  strip: {
    display: 'flex',
    gap: 6,
    overflowX: 'auto',
    padding: '0 12px 10px',
    scrollbarWidth: 'thin',
    scrollbarColor: '#ddd transparent',
    '&::-webkit-scrollbar': {
      height: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#ddd',
      borderRadius: 2,
    },
  },
  thumb: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
    cursor: 'pointer',
    '&:hover $thumbImg': {
      borderColor: '#9fa8da',
    },
  },
  thumbImg: {
    width: 54,
    height: 40,
    borderRadius: 4,
    border: '2px solid #e0e0e0',
    objectFit: 'cover',
    background: '#eeeeee',
    display: 'block',
    transition: 'border-color 0.15s ease',
  },
  thumbImgActive: {
    borderColor: '#3F51B5',
  },
  thumbPlaceholder: {
    width: 54,
    height: 40,
    borderRadius: 4,
    border: '2px solid #e0e0e0',
    background: '#eeeeee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: '#aaa',
    transition: 'border-color 0.15s ease',
    flexShrink: 0,
  },
  thumbPlaceholderActive: {
    borderColor: '#3F51B5',
    background: '#e8eaf6',
    color: '#3F51B5',
  },
  thumbMeta: {
    fontSize: 9,
    color: '#aaa',
    textAlign: 'center',
    maxWidth: 60,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  thumbMetaActive: {
    color: '#3F51B5',
    fontWeight: 600,
  },
}));

/**
 * PageNavigator — horizontal scrollable strip of plan page thumbnails.
 *
 * Props:
 *   pages        – [{ page: number, url: string, title: string }]
 *   activePage   – page number of the currently displayed page
 *   onSelectPage – (page: number) => void
 */
const PageNavigator = ({ pages = [], activePage, onSelectPage }) => {
  const classes = useStyles();
  const stripRef = useRef(null);

  if (pages.length === 0) return null;

  return (
    <div className={classes.root}>
      {pages.length > 1 && (
        <div className={classes.label}>Pages</div>
      )}
      <div className={classes.strip} ref={stripRef}>
        {pages.map((p) => {
          const isActive = p.page === activePage;
          const shortTitle = p.title
            ? p.title.length > 8 ? p.title.slice(0, 7) + '…' : p.title
            : `P${p.page}`;

          return (
            <Tooltip
              key={p.page}
              title={p.title || `Page ${p.page}`}
              placement="bottom"
            >
              <div
                className={classes.thumb}
                onClick={() => onSelectPage && onSelectPage(p.page)}
              >
                {p.url ? (
                  <img
                    src={p.url}
                    alt={p.title || `Page ${p.page}`}
                    className={`${classes.thumbImg} ${isActive ? classes.thumbImgActive : ''}`}
                    draggable={false}
                  />
                ) : (
                  <div
                    className={`${classes.thumbPlaceholder} ${isActive ? classes.thumbPlaceholderActive : ''}`}
                  >
                    {p.page}
                  </div>
                )}
                <span className={`${classes.thumbMeta} ${isActive ? classes.thumbMetaActive : ''}`}>
                  {shortTitle}
                </span>
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default PageNavigator;
